// How much of a sale the customer is paying with points and store credit.
//
// TWO SOURCES, BECAUSE A REDEMPTION IS RECORDED IN TWO SHAPES over the life of
// an order, and only one of them exists at a time:
//
//   before the first save   the cashier form's 折抵 boxes, a POSITIVE number
//   after it                the `awards` array, a SIGNED delta
//
// The boxes are screen state: `withoutAwards` strips them from every save, so
// a saved order carries no VALUE for them however the schema declares them --
// the awards array IS the record. Reading only the boxes therefore answered 0
// for every reopened order, and since `amount` is derived from this figure, one
// keystroke on a reopened order silently restored the GROSS total and the
// re-checkout issued a 統一發票 for it. See the spec for the case.
//
// A REDUCTION OF THE SALE, NOT TENDER. This comes off items[].amount alongside
// the whole-order discount, so the 統一發票 declares what the customer actually
// paid. That is a tax position and it is deliberate: redemption reduces the
// invoice. The alternative reading -- points are tender, so the invoice keeps
// the gross amount -- was what the code did before, by never having decided.
//
// A POINT COUNTS AS NT$1, and so does a unit of store credit. CONFIRMED
// 2026-08-25 -- it had been an assumption inherited from the points suggestion,
// and it matters more now that it sets the figure the 統一發票 declares. If a
// rate ever differs by vendor, this is the line that has to grow one.

// Number(null) and Number('') are both 0, which is what we want for a field the
// staff cleared; Number(undefined) is NaN, which is not.
const asNumber = raw => {
  const value = Number(raw)

  return Number.isFinite(value) ? value : 0
}

const at = (doc, path) =>
  path.reduce(
    (node, key) =>
      node === null || node === undefined ? undefined : node[key],
    doc,
  )

const REDEEMED = [
  ['spendPoints', 'value'],
  ['spendCredits', 'value'],
]

const fromForm = values =>
  REDEEMED.reduce((total, path) => total + asNumber(at(values, path)), 0)

// DIRECTION COMES FROM `intent`, NEVER FROM THE SIGN -- the same rule awardRows
// follows, and the reason `intent` is stored at all. A return is entered as a
// new order with negative quantities, so 贈送點數 -12 is a clawback: negative,
// and not a redemption. Reading the sign would count it as one and take another
// NT$12 off a refund.
//
// Awards written before `intent` existed have none, and the sign is all those
// ever had. Clawbacks postdate `intent`, so the fallback cannot meet one.
const isSpend = award => {
  if (award.intent) return award.intent === 'spend'

  return asNumber(award.value) < 0
}

// Stored signed, read back positive -- the boxes' convention, which is what the
// callers are built around.
//
// A GIVEN-UP AWARD STILL COUNTS. What the customer paid was settled at the
// till; whether the platform later accepted the deduction is a separate problem
// with its own repair, and re-pricing a completed sale behind the staff's back
// is not part of it.
const fromAwards = awards =>
  awards.reduce(
    (total, award) => (isSpend(award) ? total - asNumber(award.value) : total),
    0,
  )

// Always a number, never null: "nothing was redeemed" and "no redemption field
// yet" are the same thing to a running total.
//
// THE AWARDS WIN ONLY ON A SAVED ORDER. `_id` is what says the order has been
// through the server, and it is the same line the server draws: awardIntake
// builds the array on POST and drops every award field on PATCH. Without that
// condition a 複製 would read its source's awards -- copied into the new
// document, `_id` cleared -- and discount a fresh sale by a redemption that
// belongs to another one.
//
// An empty array needs no clause of its own: a saved order that redeemed
// nothing has no boxes either, so both sources answer 0.
export default function redeemedAmount(values) {
  const awards = values ? values.awards : null

  if (values && values._id && Array.isArray(awards)) return fromAwards(awards)

  return fromForm(values)
}

export { REDEEMED }
